import { endOfDay, startOfDay } from 'date-fns'

// import type { PlaylistReportState } from '@disco/state-management/reducers/reports/playlistReport'
type PlaylistReportState = any

/** Maximum number of records that can be returned in a single report query. */
export const REPORT_LIMIT = 10000

/**
 * Builds a query object for playlist reports based on form parameters.
 *
 * Converts the report form state into a query object that can be sent to the API.
 * Handles various filtering options, date ranges, and output formats.
 *
 * @param reportParams - The report parameters from the form state
 * @param format - The desired output format ('json_table', 'csv', etc.)
 * @returns Query object ready for API submission
 *
 * @example
 * ```typescript
 * const reportParams = {
 *   form: {
 *     startDate: new Date('2024-01-01'),
 *     endDate: new Date('2024-01-31'),
 *     playlistTitle: 'My Playlist',
 *     selectedChannels: [{ value: 'channel1' }],
 *     // ... other form fields
 *   },
 *   visibleColumns: ['title', 'artist', 'plays'],
 *   customFieldsColumns: ['custom_field_1']
 * }
 *
 * const query = buildPlaylistReportQuery(reportParams, 'csv')
 * // Returns query object with all filters and parameters applied
 * ```
 */
export function buildPlaylistReportQuery(
  reportParams: PlaylistReportState,
  format: string,
): any {
  const {
    startDate,
    endDate,
    playlistTitle,
    generalMetadataValue,
    advancedMetadataKey,
    advancedMetadataValue,
    selectedChannels,
    selectedTerritories,
    writer,
    sharedWith,
    owner,
    selectedTags,
    isClientOnly,
    force_all_results_secret_backdoor,
  } = reportParams.form
  const { customFieldsColumns } = reportParams
  let { visibleColumns } = reportParams

  if (visibleColumns.includes('access_stats')) {
    visibleColumns = visibleColumns
      .filter(x => x !== 'access_stats')
      .concat([
        'access_playlist',
        'download_zip',
        'download_track',
        'play_track',
      ])
  }

  let count: number | undefined = undefined
  switch (format) {
    case 'json_table': // preview
      count = 100
      break
  }

  const query: any = {
    count,
    type: 'playlist' as const,
    start_date: startOfDay(startDate).toISOString(),
    end_date: endOfDay(endDate).toISOString(),
    group_ids: undefined as string | undefined,
    territory_ids: undefined as string | undefined,
    tags: undefined as string | undefined,
    // q='' works same as q=undefined
    q: undefined as string | undefined,
    meta_any: undefined as string | undefined,
    is_client: undefined as boolean | undefined,
    filters: undefined as string | undefined,
    internal_comment: undefined as string | undefined,
    client_comment: undefined as string | undefined,
    writer_id: undefined as string | undefined,
    shared_with_contact_id: undefined as string | undefined,
    owner_id: undefined as string | undefined,
    output_format: format,
    visible_columns: JSON.stringify(visibleColumns),
    force_all_results_secret_backdoor: undefined as boolean | undefined,
    include_custom_fields: JSON.stringify(customFieldsColumns),
  }

  if (selectedChannels.length > 0) {
    query.group_ids = JSON.stringify(selectedChannels.map(group => group.value))
  }

  if (selectedTerritories.length > 0) {
    query.territory_ids = JSON.stringify(
      selectedTerritories.map(group => group.value),
    )
  }

  if (selectedTags.length > 0) {
    query.tags = selectedTags.map(tag => tag.value).join(',')
  }

  query.writer_id = writer || undefined

  query.shared_with_contact_id = sharedWith || undefined

  query.owner_id = owner || undefined

  const filters: Record<string, any> = {}
  if (isClientOnly) {
    query.is_client = true
  }

  if (force_all_results_secret_backdoor && format !== 'json_table') {
    query.force_all_results_secret_backdoor = true
  }

  if (reportParams.sectionExpanded.advancedMetadata) {
    if (
      advancedMetadataKey === 'disco_internal_comment' ||
      advancedMetadataKey === 'disco_client_comment'
    ) {
      query[advancedMetadataKey.replace(/^disco_/, '')] =
        advancedMetadataValue || undefined
    } else {
      filters[advancedMetadataKey] = {
        value:
          (typeof advancedMetadataValue === 'boolean'
            ? String(advancedMetadataValue)
            : advancedMetadataValue) || undefined,
      }
    }
  }
  query.meta_any = generalMetadataValue || undefined

  query.q = playlistTitle || undefined

  // Checking if filters are empty because API returns HTTP 500 for filters=[]
  if (Object.keys(filters).length > 0) {
    query.filters = JSON.stringify(filters)
  }
  return query
}
